import {
  ensureNotFalsy,
  getFromMapOrCreate,
  PROMISE_RESOLVE_VOID,
  randomCouchString,
} from 'rxdb/plugins/core';

import makeLogger from '../../../../utils/makeLogger';
import { boolParamsToInt } from './sqlite-helpers';
import type {
  Sqlite3Type,
  SQLiteBasics,
  SQLiteDatabaseClass,
  SQLiteQueryWithParams,
} from './sqlite-types';

const BASICS_BY_SQLITE_LIB = new WeakMap();
export function getSQLiteBasicsNode(sqlite3: Sqlite3Type): SQLiteBasics<SQLiteDatabaseClass> {
  let basics: SQLiteBasics<SQLiteDatabaseClass> = BASICS_BY_SQLITE_LIB.get(sqlite3);
  if (!basics) {
    basics = {
      open: (name: string) => Promise.resolve(new sqlite3.Database(name)),
      async run(db: SQLiteDatabaseClass, queryWithParams: SQLiteQueryWithParams) {
        if (!Array.isArray(queryWithParams.params)) {
          throw new Error(`no params array given for query: ${queryWithParams.query}`);
        }
        await execSqlSQLiteNode(db, queryWithParams, 'run');
      },
      async all(db: SQLiteDatabaseClass, queryWithParams: SQLiteQueryWithParams) {
        const result = await execSqlSQLiteNode(db, queryWithParams, 'all');
        return result;
      },
      async setPragma(db, key, value) {
        return execSqlSQLiteNode(
          db,
          {
            query: `PRAGMA ${key} = ${value}`,
            params: [],
            context: {
              method: 'setPragma',
              data: {
                key,
                value,
              },
            },
          },
          'run',
        );
      },
      close(db: SQLiteDatabaseClass) {
        return closeSQLiteDatabaseNode(db);
      },
      journalMode: 'WAL2',
    };
    BASICS_BY_SQLITE_LIB.set(sqlite3, basics);
  }
  return basics;
}

/**
 * Promisified version of db.run()
 */
export function execSqlSQLiteNode(
  database: SQLiteDatabaseClass,
  queryWithParams: SQLiteQueryWithParams,
  operator: 'run' | 'all',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  const debug = false;
  let resolved = false;
  // eslint-disable-next-line promise/param-names
  return new Promise((res, rej) => {
    if (debug) {
      // eslint-disable-next-line no-console
      console.log(`# execSqlSQLiteNode() ${queryWithParams.query}`);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    database[operator](queryWithParams.query, queryWithParams.params, (err: any, result: any) => {
      if (resolved) {
        throw new Error(`callback called mutliple times ${queryWithParams.query}`);
      }
      resolved = true;
      if (err) {
        if (debug) {
          // eslint-disable-next-line no-console
          console.log('---- ERROR RUNNING SQL:');
          // eslint-disable-next-line no-console
          console.log(queryWithParams.query);
          // eslint-disable-next-line no-console
          console.dir(queryWithParams.params);
          // eslint-disable-next-line no-console
          console.dir(err);
          // eslint-disable-next-line no-console
          console.log('----');
        }
        rej(err);
      } else {
        if (debug) {
          // eslint-disable-next-line no-console
          console.log(`execSql() result: ${database.eventNames()}`);
          // eslint-disable-next-line no-console
          console.log(queryWithParams.query);
          // eslint-disable-next-line no-console
          console.dir(result);
          // eslint-disable-next-line no-console
          console.log('execSql() result:');
          // eslint-disable-next-line no-console
          console.log(queryWithParams.query);
          // eslint-disable-next-line no-console
          console.dir(queryWithParams.params);
          // eslint-disable-next-line no-console
          console.log('execSql() result -------------------------');
        }
        res(result);
      }
    });
  });
}

export function closeSQLiteDatabaseNode(database: SQLiteDatabaseClass): Promise<void> {
  // eslint-disable-next-line promise/param-names
  return new Promise((res, rej) => {
    let resolved = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    database.close((err: any) => {
      if (resolved) {
        throw new Error('close() callback called mutliple times');
      }
      resolved = true;
      if (err && !err.message.includes('Database is closed')) {
        rej(err);
      } else {
        res();
      }
    });
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SQLiteCapacitorDatabase = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SQLiteConnection = any;

const BASICS_BY_SQLITE_LIB_CAPACITOR: WeakMap<
  SQLiteConnection,
  SQLiteBasics<SQLiteCapacitorDatabase>
> = new WeakMap();
const CAPACITOR_CONNECTION_BY_NAME = new Map();

/**
 * In capacitor it is not allowed to reopen an already
 * open database connection. So we have to queue the open-close
 * calls so that they do not run in parallel and we do not open&close
 * database connections at the same time.
 */
let capacitorOpenCloseQueue = PROMISE_RESOLVE_VOID;

export function getSQLiteBasicsCapacitor(
  sqlite: SQLiteConnection,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  capacitorCore: any,
): SQLiteBasics<SQLiteCapacitorDatabase> {
  const basics = getFromMapOrCreate<SQLiteConnection, SQLiteBasics<SQLiteCapacitorDatabase>>(
    BASICS_BY_SQLITE_LIB_CAPACITOR,
    sqlite,
    () => {
      const innerBasics: SQLiteBasics<SQLiteCapacitorDatabase> = {
        open(dbName: string) {
          capacitorOpenCloseQueue = capacitorOpenCloseQueue.then(async () => {
            const db = await getFromMapOrCreate(CAPACITOR_CONNECTION_BY_NAME, dbName, () =>
              sqlite.createConnection(dbName, false, 'no-encryption', 1));
            await db.open();
            return db;
          });
          return capacitorOpenCloseQueue;
        },
        async run(db: SQLiteCapacitorDatabase, queryWithParams: SQLiteQueryWithParams) {
          await db.run(queryWithParams.query, queryWithParams.params, false);
        },
        async all(db: SQLiteCapacitorDatabase, queryWithParams: SQLiteQueryWithParams) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result: any = await db.query(queryWithParams.query, queryWithParams.params);
          return ensureNotFalsy(result.values);
        },
        async setPragma(db, key, value) {
          return db.execute(`PRAGMA ${key} = ${value}`, false);
        },
        close(db: SQLiteCapacitorDatabase) {
          capacitorOpenCloseQueue = capacitorOpenCloseQueue.then(() => {
            return db.close();
          });
          return capacitorOpenCloseQueue;
        },

        /**
         * On android, there is already WAL mode set.
         * So we do not have to set it by our own.
         * @link https://github.com/capacitor-community/sqlite/issues/258#issuecomment-1102966087
         */
        journalMode: capacitorCore.getPlatform() === 'android' ? '' : 'WAL',
      };
      return innerBasics;
    },
  );
  return basics;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OpSQLiteDatabase = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OpSQLiteConnection = any;
const logger = makeLogger(__filename, { shouldLogToNewRelic: false, shouldLog: false });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getSQLiteBasicsOpSQLite(openDB: any, location?: string): SQLiteBasics<OpSQLiteDatabase> {
  return {
    open: async (name: string) => {
      logger.info('Opening SQLite connection ', { name, location });
      return openDB(location ? { name, location } : { name });
    },
    all: async (db: OpSQLiteConnection, queryWithParams: SQLiteQueryWithParams) => {
      logger.info('all: ', { queryWithParams });
      const result = await db.execute(queryWithParams.query, queryWithParams.params);
      return result.rows;
    },
    run: async (db: OpSQLiteConnection, queryWithParams: SQLiteQueryWithParams) => {
      logger.info('run: ', { queryWithParams });
      return db.execute(queryWithParams.query, queryWithParams.params);
    },
    async setPragma(db, key, value) {
      logger.info('setPragma: ', { key, value });
      return db.execute(`PRAGMA ${key} = ${value}`, []);
    },
    async pragma(db, cmd) {
      logger.info('run Pragma: ', { cmd });
      return db.execute(`PRAGMA ${cmd};`, []);
    },
    close: async (db: OpSQLiteConnection) => {
      logger.info('close: ');
      db.close();
    },
    journalMode: '',
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getSQLiteBasicsExpoSQLite(openDB: any): SQLiteBasics<any> {
  return {
    open: async (name: string) => {
      return Promise.resolve(openDB(name));
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    all: async (db: any, queryWithParams: SQLiteQueryWithParams) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = new Promise<any>((resolve, reject) => {
        db.exec(
          [{ sql: queryWithParams.query, args: queryWithParams.params }],
          false,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (err: any, res: any) => {
            if (err) {
              return reject(err);
            }
            if (Array.isArray(res)) {
              const queryResult = res[0]; // there is only one query
              if (Object.prototype.hasOwnProperty.call(queryResult, 'rows')) {
                return resolve(queryResult.rows);
              }
              return reject(queryResult.error);
            }
            return reject(new Error(`getSQLiteBasicsExpoSQLite.all() response is not an array: ${res}`));
          },
        );
      });
      return result;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    run: async (db: any, queryWithParams: SQLiteQueryWithParams) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return new Promise<any>((resolve, reject) => {
        db.exec(
          [{ sql: queryWithParams.query, args: queryWithParams.params }],
          false,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (err: any, res: any) => {
            if (err) {
              return reject(err);
            }
            if (Array.isArray(res) && res[0] && res[0].error) {
              return reject(res);
            } else {
              resolve(res);
            }
          },
        );
      });
    },
    setPragma(db, key, value) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return new Promise<any>((resolve, reject) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        db.exec([{ sql: `pragma ${key} = ${value};`, args: [] }], false, (err: any, res: any) => {
          if (err) {
            return reject(err);
          }
          if (Array.isArray(res) && res[0] && res[0].error) {
            return reject(res);
          } else {
            resolve(res);
          }
        });
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    close: async (db: any) => {
      return db.closeAsync();
    },
    journalMode: '',
  };
}

/**
 * Build to be compatible with packages
 * that use the websql npm package like:
 * @link https://www.npmjs.com/package/react-native-sqlite-2
 * @link https://www.npmjs.com/package/websql
 * Use like:
 * import SQLite from 'react-native-sqlite-2';
 * getRxStorageSQLite({
 *   sqliteBasics: getSQLiteBasicsWebSQL(SQLite.openDatabase)
 * });
 *
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getSQLiteBasicsWebSQL(openDB: any): SQLiteBasics<any> {
  return {
    open: async (name: string) => {
      const webSQLDatabase = await openDB(name, '1.0', '', 1);
      return ensureNotFalsy(webSQLDatabase._db);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    all: async (db: any, queryWithParams: SQLiteQueryWithParams) => {
      const rawResult = await webSQLExecuteQuery(db, queryWithParams);
      const rows = Array.from(rawResult.rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return rows as any;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    run: async (db: any, queryWithParams: SQLiteQueryWithParams) => {
      await webSQLExecuteQuery(db, queryWithParams);
    },
    setPragma: async (db, key, value) => {
      await webSQLExecuteQuery(db, {
        query: `pragma ${key} = ${value};`,
        params: [],
        context: {
          method: 'setPragma',
          data: {
            key,
            value,
          },
        },
      }).catch((err) => {

        /**
         * WebSQL in the browser does not allow us to set any pragma
         * so we have to catch the error.
         * @link https://stackoverflow.com/a/10298712/3443137
         */
        if (err.message.includes('23 not authorized')) {
          return;
        }
        throw err;
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    close: async (db: any) => {

      /**
       * The WebSQL API itself has no close() method.
       * But some libraries have different custom close methods.
       */
      if (typeof db.closeAsync === 'function') {
        return db.closeAsync();
      }
      if (typeof db.close === 'function') {
        return db.close();
      }
    },
    journalMode: '',
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function webSQLExecuteQuery(db: any, queryWithParams: SQLiteQueryWithParams): Promise<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return new Promise<any>((resolve, reject) => {
    db.exec(
      [{ sql: queryWithParams.query, args: queryWithParams.params }],
      false,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (err: any, res: any) => {
        if (err) {
          return reject(err);
        }
        if (Array.isArray(res) && res[0] && res[0].error) {
          return reject(res[0].error);
        } else {
          return resolve(res[0]);
        }
      },
    );
  });
}

/**
 * Build to be compatible with packages
 * that use SQLite compiled to webassembly:
 * @link https://github.com/rhashimoto/wa-sqlite
 * Use like:
 * ```ts
 * import sqlite3InitModule from '@sqlite.org/sqlite-wasm';
 * const sqliteWasm = await sqlite3InitModule();
 * getSQLiteBasicsWasm({ dbConstructor: sqliteWasm.oo1.DB });
 * ```
 *
 */

/**
 * TODO the wa-sqlite module has problems
 * when running prepared statements with params
 * in parallel. So we de-parrallel the runs here.
 * This is bad for performance and should be fixed at the
 * wa-sqlite repo.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let runQueueWasmSQLite: Promise<any> = PROMISE_RESOLVE_VOID;
export type WasmSqliteDb = { nr: number; name: string; };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getSQLiteBasicsWasm(sqlite3: any): SQLiteBasics<WasmSqliteDb> {
  const debugId = randomCouchString(5);
  // eslint-disable-next-line no-console
  console.log(`getSQLiteBasicsWasm() debugId: ${debugId}`);
  return {
    debugId,
    open: async (name: string) => {
      const newQueue = runQueueWasmSQLite.then(async () => {
        const dbNr = await sqlite3.open_v2(name);
        return { nr: dbNr, name };
      });
      runQueueWasmSQLite = newQueue.catch(() => {});
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return newQueue as any;
    },
    all: async (db, queryWithParams: SQLiteQueryWithParams) => {
      const newQueue = runQueueWasmSQLite.then(async () => {
        const result = await sqlite3.execWithParams(
          db.nr,
          queryWithParams.query,
          boolParamsToInt(queryWithParams.params),
        );
        return result.rows;
      });
      runQueueWasmSQLite = newQueue.catch(() => {});
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return newQueue as any;
    },
    run: async (db, queryWithParams: SQLiteQueryWithParams) => {
      const newQueue = runQueueWasmSQLite.then(async () => {
        sqlite3.run(db.nr, queryWithParams.query, queryWithParams.params);
        // return new Promise(async (res) => {
        //     console.log('run start! ' + queryWithParams.query);
        //     const runResult = await sqlite3.run(db.nr, queryWithParams, (a1: any, a2: any) => {
        //         console.log('run result ccallback:');
        //         console.log(JSON.stringify({ a1, a2 }));
        //         res();
        //     });
        //     console.log('runResult:');
        //     console.log(JSON.stringify(runResult));
        // });
      });
      runQueueWasmSQLite = newQueue.catch(() => {});
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return newQueue as any;
    },
    setPragma: async (db, key, value) => {
      const newQueue = runQueueWasmSQLite.then(async () => {
        await sqlite3.exec(db.nr, `pragma ${key} = ${value};`);
      });
      runQueueWasmSQLite = newQueue.catch(() => {});
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return newQueue as any;
    },
    close: async (db) => {
      const newQueue = runQueueWasmSQLite.then(async () => {
        await sqlite3.close(db.nr);
      });
      runQueueWasmSQLite = newQueue.catch(() => {});
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return newQueue as any;
    },
    journalMode: '',
  };
}
